
import { RetailItem, UserProfile, OutfitLayer, SavedOutfit, AgentRole, AgentResponse, AgentAction, OrchestratorContext } from "../types";
import { ai, STYLIST_SYSTEM_INSTRUCTION, buildPersonalizationContext } from "./gemini-core";
import { findSimilarItems } from "./geminiService";
import { SchemaType, ChatSession } from "@google/generative-ai";
import { GEMINI_CONFIG } from "../config";
import { Logger } from "./logger";

/**
 * Persists chat sessions.
 * In a production environment, this should move to a persistent store like Redis or Firestore.
 */
/**
 * Persists chat sessions with user-level isolation.
 */
class SessionManager {
    private static instance: SessionManager;
    // Map<userId, Map<sessionId, ChatSession>>
    private userSessions: Map<string, Map<string, ChatSession>> = new Map();

    private constructor() {}

    public static getInstance(): SessionManager {
        if (!SessionManager.instance) {
            SessionManager.instance = new SessionManager();
        }
        return SessionManager.instance;
    }

    public getSession(userId: string, sessionId: string): ChatSession | undefined {
        return this.userSessions.get(userId)?.get(sessionId);
    }

    public setSession(userId: string, sessionId: string, session: ChatSession) {
        if (!this.userSessions.has(userId)) {
            this.userSessions.set(userId, new Map());
        }
        this.userSessions.get(userId)!.set(sessionId, session);
    }

    /**
     * Internal registry for global orchestration across all users
     */
    public getAllActiveSessions(): { userId: string, sessionId: string, session: ChatSession }[] {
        const flat: { userId: string, sessionId: string, session: ChatSession }[] = [];
        this.userSessions.forEach((sessions, userId) => {
            sessions.forEach((session, sessionId) => {
                flat.push({ userId, sessionId, session });
            });
        });
        return flat;
    }
}

const agentTools = [
    {
        name: 'open_color_picker',
        description: 'Opens the color picker UI so the user can change the color of their current garment.',
        handler: async () => ({
            role: 'stylist' as AgentRole,
            text: "I've opened the color picker so you can adjust the look.",
            actions: [{ type: 'OPEN_COLOR_PICKER' as const, payload: {} }]
        })
    },
    {
        name: 'book_session',
        description: 'Books a styling session with a professional human stylist.',
        parameters: {
            type: SchemaType.OBJECT,
            properties: {
                serviceId: { type: SchemaType.STRING, description: 'The ID of the styling service' },
            },
            required: ['serviceId'],
        },
        handler: async (args: any) => ({
            role: 'booking' as AgentRole,
            text: "I'll help you get that scheduled. I'm passing you to our booking specialist.",
            actions: [{ type: 'BOOK_SESSION' as const, payload: { serviceId: args.serviceId || 'personal-styling' } }]
        })
    },
    {
        name: 'find_similar_items',
        description: 'Searches for retail items that are similar to the garments the user is currently wearing or interested in.',
        parameters: {
            type: SchemaType.OBJECT,
            properties: {
                query: { type: SchemaType.STRING, description: 'Specific search query or category' },
            }
        },
        handler: async (args: any, context: OrchestratorContext) => {
            const { currentLayer, userProfile } = context;
            const garment = currentLayer?.garments?.[0];
            const searchContext = args.query || garment?.nameKey || 'fashionable items';
            
            // Log status for A2UI
            const statusSteps: { message: string, status: 'complete' | 'in-progress' | 'error' }[] = [
                { message: `Searching retailers for ${searchContext}...`, status: 'in-progress' }
            ];
            
            try {
                const { results } = await findSimilarItems({ name: searchContext, url: garment?.url || '' }, undefined, userProfile);
                statusSteps[0].status = 'complete';

                return {
                    role: 'shopper' as AgentRole,
                    text: results.length > 0
                        ? `I've curated some ${searchContext} that would complement your style perfectly.`
                        : `I'm looking for ${searchContext} but couldn't find an exact match right now.`,
                    actions: results.length > 0 ? [{ type: 'SHOW_PRODUCTS' as const, payload: results }] : [],
                    statusSteps
                };
            } catch (error) {
                Logger.error("Tool: find_similar_items error", error);
                statusSteps[0].status = 'error';
                return {
                    role: 'shopper' as AgentRole,
                    text: `I had some trouble searching for ${searchContext} right now.`,
                    statusSteps
                };
            }
        }
    }
];

export class AgentOrchestrator {
    private context: OrchestratorContext;
    private userId: string;
    private sessionId: string;
    private chatSession: ChatSession | null = null;
    private sessionManager = SessionManager.getInstance();

    constructor(userId: string, context?: any, sessionId: string = 'default-session') {
        this.userId = userId;
        this.context = {
            userProfile: context?.userProfile || {},
            currentLayer: context?.currentLayer || { garments: [], accessories: [], appliedGarmentColors: {}, appliedAccessoryColors: {}, itemAdjustments: {}, poseOutputs: {}, currentPoseInstruction: '' },
            savedOutfits: context?.savedOutfits || []
        };
        this.sessionId = sessionId;
    }

    private initChatSession(forceRole?: AgentRole) {
        // Try to retrieve existing session for this specific user
        const existingSession = this.sessionManager.getSession(this.userId, this.sessionId);
        
        if (existingSession && !forceRole) {
            this.chatSession = existingSession;
            return;
        }

        const role = forceRole || 'stylist';
        const personalization = buildPersonalizationContext(this.context.userProfile);

        let roleInstruction = STYLIST_SYSTEM_INSTRUCTION;
        if (role === 'shopper') {
            roleInstruction = "You are an expert Personal Shopper. Your goal is to find the best retail products that match the user's style. Focus on brands, prices, and availability.";
        } else if (role === 'booking') {
            roleInstruction = "You are a Booking Coordinator. Help the user schedule sessions with human stylists. Be polite and efficient.";
        }

        const systemInstruction = `${roleInstruction}\n\nUSER PERSONALIZATION: ${personalization}\n\nCORE INSTRUCTIONS: You can use tools to assist the user. If they want to change color, use open_color_picker. If they want to shop or find similar items, use find_similar_items. If they want to book a human consultation, use book_session. You can also 'delegate_to' another specialist. IMPORTANT: When searching for items, prioritize results that align with the user's seasonal color and style goals.`;

        const model = ai.getGenerativeModel({
            model: GEMINI_CONFIG.FLASH_TEXT || 'gemini-1.5-flash',
            systemInstruction,
            tools: [{
                functionDeclarations: [
                    ...agentTools.map(({ name, description, parameters }) => ({ name, description, parameters })),
                    {
                        name: 'delegate_to',
                        description: 'Delegates the conversation to a specialized agent (shopper, booking, or stylist).',
                        parameters: {
                            type: SchemaType.OBJECT,
                            properties: {
                                role: { 
                                    type: SchemaType.STRING, 
                                    enum: ['stylist', 'shopper', 'booking'], 
                                    description: 'The specialist to hand off to.' 
                                }
                            },
                            required: ['role']
                        }
                    }
                ]
            }] as any
        });

        this.chatSession = model.startChat({ history: [] });
        this.sessionManager.setSession(this.userId, this.sessionId, this.chatSession);
    }

    async processMessage(query: string): Promise<AgentResponse> {
        this.initChatSession();

        const contextInfo = `User is wearing: ${this.context.currentLayer?.garments?.map(g => g.nameKey).join(', ') || 'Nothing'}. Style Goals: ${this.context.userProfile.fashionGoals || 'Not specified'}.`;
        const prompt = `[CONTEXT: ${contextInfo}]\nUser says: ${query}`;

        try {
            const result = await this.chatSession!.sendMessage(prompt);
            const response = result.response;
            const functionCalls = response.functionCalls();

            if (functionCalls && functionCalls.length > 0) {
                const call = functionCalls[0];
                const tool = agentTools.find(t => t.name === call.name);

                if (tool) {
                    const toolResult = await tool.handler(call.args, this.context);
                    const assistantText = response.text() || '';
                    return {
                        ...toolResult,
                        text: assistantText ? `${assistantText}\n\n${toolResult.text}` : toolResult.text,
                        delegatedTo: toolResult.role !== 'stylist' ? toolResult.role : undefined,
                        sessionId: this.sessionId
                    };
                }

                if (call.name === 'delegate_to') {
                    const newRole = (call.args as any).role as AgentRole;
                    this.initChatSession(newRole);
                    return {
                        role: newRole,
                        text: response.text() || `I'm handing you over to our ${newRole} specialist.`,
                        delegatedTo: newRole,
                        sessionId: this.sessionId
                    };
                }
            }

            return {
                role: 'stylist',
                text: response.text() || "I'm here to help you.",
                sessionId: this.sessionId
            };

        } catch (error: any) {
            Logger.error("Agent Orchestrator Error:", error);
            // Fallback to basic advice
            const model = ai.getGenerativeModel({ model: GEMINI_CONFIG.FLASH_TEXT });
            const fallbackResult = await model.generateContent(prompt);
            
            return { 
                role: 'stylist', 
                text: `I encountered a slight issue processing that, but as your stylist I recommend: ${fallbackResult.response.text()}`,
                sessionId: this.sessionId,
                statusSteps: [{ message: "Error occurred, falling back to basic advice", status: 'error' }]
            };
        }
    }

    /**
     * Entry point for orchestrating a specific agent.
     * Maps agentId to an initial role and processes the message.
     */
    async orchestrate(
        agentId: string,
        message: string,
        context?: any,
        onStatusChange?: (status: string, steps: string[]) => void
    ): Promise<AgentResponse> {
        // Map agentId to AgentRole
        let role: AgentRole = 'stylist';
        if (agentId === 'catalog-agent' || agentId === 'shopper') role = 'shopper';
        if (agentId === 'booking-agent' || agentId === 'booking') role = 'booking';

        // Update context if provided
        if (context) {
            this.context = { ...this.context, ...context };
        }

        // Initialize session with the mapped role
        this.initChatSession(role);
        
        // Process the message
        const response = await this.processMessage(message);
        
        // Trigger status change if provided
        if (onStatusChange && response.statusSteps) {
            const lastStep = response.statusSteps[response.statusSteps.length - 1];
            onStatusChange(lastStep.status, response.statusSteps.map(s => s.message));
        }

        return response;
    }

    /**
     * Global utility to list all active sessions across the platform.
     * Useful for administrative orchestration.
     */
    public static listAllGlobalSessions() {
        return SessionManager.getInstance().getAllActiveSessions();
    }
}
